import { Component, computed, inject, signal } from '@angular/core';
import { RouterLink } from '@angular/router';
import { ImpersonationStore } from 'shared';

import { AuthStore } from '../../features/authentication/store/auth.store';

@Component({
  selector: 'app-user-menu',
  standalone: true,
  imports: [RouterLink],
  templateUrl: './user-menu.component.html',
  styles: `
    :host {
      display: block;
      position: relative;
    }
  `,
})
export class UserMenuComponent {
  protected authStore = inject(AuthStore);
  protected impersonationStore = inject(ImpersonationStore);
  protected isOpen = signal(false);

  // Computed: effective user info (impersonated or original)
  protected effectiveUser = computed(() => {
    if (this.impersonationStore.isImpersonating()) {
      const impersonated = this.impersonationStore.impersonatedUser();
      if (impersonated) {
        // Parse name into first/last
        const nameParts = impersonated.name.split(' ');
        return {
          firstName: nameParts[0] ?? '',
          lastName: nameParts.slice(1).join(' ') ?? '',
          email: impersonated.email,
          photoUrl: undefined as string | undefined, // Impersonated users don't have photo
        };
      }
    }
    return this.authStore.user();
  });

  toggle(): void {
    this.isOpen.update((v) => !v);
  }

  close(): void {
    this.isOpen.set(false);
  }

  getUserInitials(): string {
    const user = this.effectiveUser();
    if (!user) return '?';

    const firstName = user.firstName ?? '';
    const lastName = user.lastName ?? '';

    if (firstName && lastName) {
      return `${firstName[0]}${lastName[0]}`.toUpperCase();
    }
    if (firstName) {
      return firstName.substring(0, 2).toUpperCase();
    }
    if (user.email) {
      return user.email.substring(0, 2).toUpperCase();
    }
    return '?';
  }

  getUserDisplayName(): string {
    const user = this.effectiveUser();
    if (!user) return 'User';

    if (user.firstName && user.lastName) {
      return `${user.firstName} ${user.lastName}`;
    }
    if (user.firstName) {
      return user.firstName;
    }
    return user.email ?? 'User';
  }

  getUserEmail(): string {
    const user = this.effectiveUser();
    return user?.email ?? '';
  }
}
